import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { PreferencesProvider } from "./context/PreferencesContext";
import { AudioProvider } from "./context/AudioContext";
import ConsentBanner from "./components/ConsentBanner";
import Layout from "./components/Layout";
import Home from "./components/Home";
import About from "./components/About";
import World from "./components/World";
import Journal from "./components/Journal";
import Resources from "./components/Resources";
import StartHere from "./components/StartHere";
import Contact from "./components/Contact";
import Story from "./components/Story";
import ExploreTopic from "./components/ExploreTopic";
import Foundation from "./components/Foundation";
import Perspective from "./components/Perspective";
import PerspectivesLanding from "./components/PerspectivesLanding";
import AudioCompare from "./components/AudioCompare";
import PreviewGate from "./components/PreviewGate";
import FounderFeature from "./components/FounderFeature";
import CommunityLanding from "./components/CommunityLanding";
import OutboundRedirect from "./components/OutboundRedirect";
import HeroPreview from "./components/HeroPreview";
import ComingSoon from "./components/ComingSoon";
import NotFound from "./components/NotFound";
import NewsletterConfirmed from "./components/NewsletterConfirmed";
import PrivacyPolicyPage from "./components/PrivacyPolicyPage";
import { FUTURE_MODULES } from "./config/modules";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

// Keep the canonical + og:url pointing at the preferred www host on every route,
// signalling the non-www/root as non-canonical to search engines.
const WWW_ORIGIN = "https://www.thethrivinglife101.com";
function Canonical() {
  const { pathname } = useLocation();
  useEffect(() => {
    const url = `${WWW_ORIGIN}${pathname === "/" ? "/" : pathname}`;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", url);
    const og = document.querySelector('meta[property="og:url"]');
    if (og) og.setAttribute("content", url);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <PreferencesProvider>
        <AudioProvider>
          <ScrollToTop />
          <Canonical />
          <Routes>
            <Route path="/hero-preview/:id" element={<HeroPreview />} />
            <Route
              path="*"
              element={
                <Layout>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/world" element={<World />} />
                    <Route path="/journal" element={<Journal />} />
                    <Route path="/stories/when-luxury-became-ordinary" element={<Story />} />
                    <Route path="/resources" element={<Resources />} />
                    <Route path="/start-here" element={<StartHere />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/explore/:topic" element={<ExploreTopic />} />
                    <Route path="/foundations/:slug" element={<Foundation />} />
                    {/* Perspectives — long-form editorial system. */}
                    <Route path="/perspectives" element={<PerspectivesLanding />} />
                    <Route path="/perspectives/:slug" element={<Perspective />} />
                    {/* Unlisted Preview-only editorial route (noindex + not in sitemap). */}
                    <Route path="/audio-compare" element={<AudioCompare />} />
                    {/* Editorial preview gate — collects the server-issued preview
                        token so drafts can be reviewed for the tab. Noindex. */}
                    <Route path="/preview" element={<PreviewGate />} />
                    <Route path="/community" element={<CommunityLanding />} />
                    <Route path="/community/:slug" element={<FounderFeature />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                    {/* Post-confirmation landing for newsletter subscribers arriving
                        from Kit's double-opt-in email. Noindex, nofollow. */}
                    <Route path="/newsletter/confirmed" element={<NewsletterConfirmed />} />
                    {/* Internal tracked outbound-click redirects (noindex).
                        Every /go/:slug URL is resolved from a lookup index
                        built from all community articles at startup.
                        Publishing a new redirect never requires code changes. */}
                    <Route path="/go/:slug" element={<OutboundRedirect />} />
                    {/* Permanent alias — the first Perspective is anchored to Technology.
                        The canonical URL is /perspectives/:slug; this alias replaces
                        history so the browser lands on the canonical route. */}
                    <Route
                      path="/technology/hidden-infrastructure-behind-artificial-intelligence"
                      element={<Navigate to="/perspectives/hidden-infrastructure-behind-artificial-intelligence" replace />}
                    />
                    {/* Reserved future-module routes — private until each is ready. */}
                    {FUTURE_MODULES.map((m) => (
                      <Route key={m.id} path={m.path} element={<ComingSoon module={m} />} />
                    ))}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Layout>
              }
            />
          </Routes>
          <ConsentBanner />
        </AudioProvider>
      </PreferencesProvider>
    </BrowserRouter>
  );
}

export default App;
